/**
 * Spatial index over the chart features (an R-tree, rbush), so the safety queries
 * stop scanning every feature on every call. The auto-router checks navigability
 * at thousands of grid nodes and the live alarms run every tick, so an index turns
 * those from O(features) into O(log n + hits).
 *
 * The safety functions take a `FeatureSource` — either the raw cells (full scan,
 * unchanged behaviour, what the tests pass) or a built `ChartIndex` (the fast
 * path). They prune candidates by bounding box, then apply the SAME precise check
 * (range to the focal point, point-in-polygon), so results are identical either
 * way; the index only skips features that were always going to be out of range.
 */
import RBush, { type BBox } from 'rbush';
import type { ChartFeature, ChartFeatureCollection } from '@/types';

export type { BBox };

interface Item extends BBox {
  feature: ChartFeature;
}

export class ChartIndex {
  private tree = new RBush<Item>();
  readonly size: number;
  constructor(cells: ChartFeatureCollection[]) {
    const items: Item[] = [];
    for (const cell of cells) {
      for (const f of cell.features) {
        const bb = featureBBox(f);
        if (bb) items.push({ ...bb, feature: f });
      }
    }
    this.tree.load(items);
    this.size = items.length;
  }
  search(b: BBox): ChartFeature[] {
    return this.tree.search(b).map((i) => i.feature);
  }
}

export type FeatureSource = ChartFeatureCollection[] | ChartIndex;

const WORLD: BBox = { minX: -180, minY: -90, maxX: 180, maxY: 90 };

/** Candidate features for a query box, from either an index or the raw cells. */
export function featuresIn(source: FeatureSource, bbox?: BBox): ChartFeature[] {
  if (source instanceof ChartIndex) return source.search(bbox ?? WORLD);
  const out: ChartFeature[] = [];
  for (const cell of source) for (const f of cell.features) out.push(f);
  return out;
}

/** A box of +/- radiusNm around a point [lon, lat]. */
export function bboxAround(point: [number, number], radiusNm: number): BBox {
  const dLat = radiusNm / 60;
  const dLon = radiusNm / (60 * Math.max(0.05, Math.cos((point[1] * Math.PI) / 180)));
  return { minX: point[0] - dLon, minY: point[1] - dLat, maxX: point[0] + dLon, maxY: point[1] + dLat };
}

/** A box around a leg a->b, padded by padNm either side. */
export function legBBox(a: [number, number], b: [number, number], padNm: number): BBox {
  const lat = (a[1] + b[1]) / 2;
  const dLat = padNm / 60;
  const dLon = padNm / (60 * Math.max(0.05, Math.cos((lat * Math.PI) / 180)));
  return {
    minX: Math.min(a[0], b[0]) - dLon,
    minY: Math.min(a[1], b[1]) - dLat,
    maxX: Math.max(a[0], b[0]) + dLon,
    maxY: Math.max(a[1], b[1]) + dLat,
  };
}

/** Bounding box of any GeoJSON geometry's coordinates, or null if it has none. */
export function featureBBox(f: ChartFeature): BBox | null {
  const g = f.geometry as { coordinates?: unknown } | undefined;
  if (!g || g.coordinates == null) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const visit = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      const x = c[0] as number;
      const y = c[1] as number;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    } else if (Array.isArray(c)) {
      for (const cc of c) visit(cc);
    }
  };
  visit(g.coordinates);
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}
